import { ErrorCode } from '@gr/core/errors';
import type { CreatePlanRequestDto, PlanDto, PlanLimitsInput, UpdatePlanRequestDto } from '@gr/core/accounts/plans/dto';
import { PLAN_LIMIT_MAX, PLAN_LIMITS, type PlanLimit } from '@gr/core/accounts/plans/limits';

// PLAN-UC-1, PLAN-UC-1.CC-1: форма плана — строки лимитов ↔ DTO, без React.

export interface PlanFormValues {
  code: string;
  name: string;
  description: string;
  /** Число лимита текстом поля; путь «limits.<лимит>» совпадает с ошибкой сервера. */
  limits: Record<PlanLimit, string>;
  /** «Без ограничения» — значение лимита null. */
  unlimited: Record<PlanLimit, boolean>;
}

const byLimit = <T>(value: (limit: PlanLimit) => T): Record<PlanLimit, T> => Object.fromEntries(PLAN_LIMITS.map((limit) => [limit, value(limit)])) as Record<PlanLimit, T>;

/** Значения формы: у нового плана все лимиты «без ограничения». */
export function planFormValues(plan?: PlanDto): PlanFormValues {
  return {
    code: plan?.code ?? '',
    name: plan?.name ?? '',
    description: plan?.description ?? '',
    limits: byLimit((limit) => (plan && plan.limits[limit] !== null ? String(plan.limits[limit]) : '')),
    unlimited: byLimit((limit) => !plan || plan.limits[limit] === null),
  };
}

/** Проверка числа лимита до отправки: целое от 0; при «без ограничения» не проверяется. */
export function limitInputError(text: string, unlimited: boolean): ErrorCode | null {
  if (unlimited) return null;
  const value = text.trim();
  if (!/^\d+$/.test(value)) return ErrorCode.PlanLimitInvalid;
  return Number(value) <= PLAN_LIMIT_MAX ? null : ErrorCode.PlanLimitInvalid;
}

function limitsOf(values: PlanFormValues): PlanLimitsInput {
  return byLimit((limit) => (values.unlimited[limit] ? null : Number(values.limits[limit].trim())));
}

export function createPlanRequest(values: PlanFormValues): CreatePlanRequestDto {
  return { code: values.code.trim(), name: values.name, description: values.description, limits: limitsOf(values) };
}

/** Код после создания не меняется (PLAN-UC-1.CC-1) — в правку не входит. */
export function updatePlanRequest(values: PlanFormValues): UpdatePlanRequestDto {
  return { name: values.name, description: values.description, limits: limitsOf(values) };
}

/** Поля формы для applyServerErrors. */
export const PLAN_FORM_FIELDS = ['code', 'name', 'description', 'limits'] as const;
